import React, {useState, useRef, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import Swatch from '../swatch-group/swatch'
import SwatchGroup from '../swatch-group'
import {Box, Text, Button} from '@salesforce/retail-react-app/app/components/shared/ui'

const UnselectedVariationGroups = ({
    variationAttributes = [],
    setFindInStoreModalTitle,
    setIsVariantSelected
}) => {
    const intl = useIntl()
    const initialMissingIdsRef = useRef(null)

    if (initialMissingIdsRef.current === null) {
        initialMissingIdsRef.current = variationAttributes
            .filter(({selectedValue}) => !selectedValue?.value)
            .map(({id}) => id)
    }

    const initialMissingIds = initialMissingIdsRef.current
    const [isButtonDisabled, setIsButtonDisabled] = useState(true)

    useEffect(() => {
        const stillMissing = initialMissingIds.some((id) => {
            const group = variationAttributes.find((attr) => attr.id === id)
            return !group?.selectedValue?.value
        })
        setIsButtonDisabled(stillMissing)
    }, [variationAttributes, initialMissingIds])

    useEffect(() => {
        let modalTitle = intl.formatMessage(
            {
                id: 'pdp_find_in_store.select.attr.title',
                defaultMessage: 'select {missingAttr}'
            },
            {missingAttr: initialMissingIdsRef.current}
        )

        if (initialMissingIds.includes('length') > 0 && initialMissingIds.includes('size') > 0) {
            modalTitle = intl.formatMessage(
                {
                    id: 'pdp_find_in_store.select.attr.title.with.length',
                    defaultMessage: 'Select {size} and {length}'
                },
                {size: initialMissingIds[0], length: initialMissingIds[1]}
            )
        }
        setFindInStoreModalTitle(modalTitle)
    }, [])

    return (
        <>
            <Text fontWeight="normal" fontSize="sm" mb="38px">
                {initialMissingIds.includes('length') > 0 &&
                initialMissingIds.includes('size') > 0 ? (
                    <FormattedMessage
                        defaultMessage="Select a {size} and {length} to check availability in stores."
                        id="pdp_find_in_store.select.attr.text.width.length"
                        values={{size: initialMissingIds[0], length: initialMissingIds[1]}}
                    />
                ) : (
                    <FormattedMessage
                        defaultMessage="Select one {missingAttr} to check availability in stores"
                        id="pdp_find_in_store.select.attr.text"
                        values={{missingAttr: initialMissingIdsRef.current}}
                    />
                )}
            </Text>

            <Box display="flex" flexDirection="column" gap="40px">
                {variationAttributes
                    .filter(({id}) => initialMissingIds.includes(id))
                    .map(({id, name, values, selectedValue}) => (
                        <SwatchGroup
                            key={id}
                            value={selectedValue?.value || ''}
                            displayName={name}
                            isPDP
                            isSizeSwatch={id !== 'color'}
                            id={id}
                        >
                            {values.map(({href, name, image, value, orderable}, index) => {
                                const content = image ? (
                                    <Box
                                        height="100%"
                                        width="100%"
                                        minWidth="32px"
                                        backgroundRepeat="no-repeat"
                                        backgroundSize="cover"
                                        backgroundColor={name.toLowerCase()}
                                        backgroundImage={`url(${image.disBaseLink || image.link})`}
                                    />
                                ) : (
                                    name
                                )

                                return (
                                    <Swatch
                                        isPDP
                                        key={value}
                                        href={href}
                                        disabled={!orderable}
                                        value={value}
                                        name={name}
                                        variant={id === 'color' ? 'circle' : 'square'}
                                        selected={selectedValue?.value === value}
                                        isFocusable={index === 0}
                                        id={id}
                                    >
                                        {content}
                                    </Swatch>
                                )
                            })}
                        </SwatchGroup>
                    ))}
            </Box>

            <Button
                mt="38px"
                width="100%"
                fontSize="md"
                fontWeight="bold"
                fontFamily="HelveticaNeueLTStd"
                borderRadius={0}
                backgroundColor="black"
                color="white"
                isDisabled={isButtonDisabled}
                onClick={() => {
                    setIsVariantSelected(true)
                }}
            >
                <FormattedMessage
                    defaultMessage="Continue"
                    id="pdp_find_in_store.button.continue"
                />
            </Button>
        </>
    )
}

UnselectedVariationGroups.propTypes = {
    variationAttributes: PropTypes.array,
    setFindInStoreModalTitle: PropTypes.func,
    setIsVariantSelected: PropTypes.func
}

export default UnselectedVariationGroups
